import { passwordSchema } from "@pcj/shared";
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Banner, Button, Field, inputClass } from "../../components/ui";
import { authClient, authErrorMessage } from "../../lib/auth-client";
import { queryKeys } from "../../lib/queries";
import { AuthLayout } from "./AuthLayout";

export function SignupPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [key]: e.target.value });

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    const password = passwordSchema.safeParse(form.password);
    if (!password.success) return setError(password.error.issues[0]?.message ?? "Contraseña inválida");
    if (form.password !== form.confirm) return setError("Las contraseñas no coinciden.");

    setBusy(true);
    const { data, error: signUpError } = await authClient.signUp.email({
      name: form.name.trim(),
      email: form.email.trim(),
      password: form.password,
      // Where the emailed confirmation link lands (already signed in).
      callbackURL: "/",
    });
    setBusy(false);
    if (signUpError) return setError(authErrorMessage(signUpError));
    if (data?.token) {
      // Email verification disabled on this server: already signed in.
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });
      navigate("/", { replace: true });
    } else {
      navigate(`/check-email?email=${encodeURIComponent(form.email.trim())}`, { replace: true });
    }
  }

  return (
    <AuthLayout title="Crear cuenta" subtitle="Para responsables de cumplimiento de las joyerías socias.">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Tu nombre">
          {(id) => (
            <input
              id={id}
              autoComplete="name"
              required
              minLength={2}
              className={inputClass}
              value={form.name}
              onChange={set("name")}
            />
          )}
        </Field>
        <Field label="Correo electrónico">
          {(id) => (
            <input
              id={id}
              type="email"
              autoComplete="email"
              required
              className={inputClass}
              value={form.email}
              onChange={set("email")}
            />
          )}
        </Field>
        <Field label="Contraseña" hint="Mínimo 10 caracteres.">
          {(id) => (
            <input
              id={id}
              type="password"
              autoComplete="new-password"
              required
              className={inputClass}
              value={form.password}
              onChange={set("password")}
            />
          )}
        </Field>
        <Field label="Confirma la contraseña">
          {(id) => (
            <input
              id={id}
              type="password"
              autoComplete="new-password"
              required
              className={inputClass}
              value={form.confirm}
              onChange={set("confirm")}
            />
          )}
        </Field>
        {error && <Banner tone="error">{error}</Banner>}
        <Button type="submit" loading={busy} className="w-full">
          Registrarme
        </Button>
      </form>
      <p className="mt-4 text-xs text-gray-600">
        <Link to="/login" className="underline hover:text-gray-900">
          Ya tengo cuenta, iniciar sesión
        </Link>
      </p>
    </AuthLayout>
  );
}
